import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  useDispatch,
  useSelector } from "react-redux";
import { setCurrentPage,
  setPageSize } from "../../../redux/znsEvent/znsEvent";
import {
  getListZnsEvent,
  updateZnsEvent,
  deleteZnsEvent,
  } from "../../../api/znsEvent/znsEvent";
import { NavLink } from "react-router-dom";
import {
  SaveOutlined,
  CloseOutlined,
  PlusCircleOutlined,
  TagOutlined,
  SearchOutlined,
  } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Table,
  Form,
  Input,
  message,
  Tooltip,
  Col,
  Row,
  Card,
  Space,
  Typography,
} from "antd";
import FormAddZnsEvent from "./FormAddZnsEvent";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import StatusTag from "../../../components/cms/StatusTag";

export default function ZnsEventTable() {
  const { listZnsEvents, isLoadZnsEvent, currentPage, pageSize, totalRecords } =
    useSelector((state) => state.persistedReducer.znsEventSlice);

  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [editedRecord, setEditedRecord] = useState({});
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  // Mở modal thêm mới
  const showModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  // Đóng modal
  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Load dữ liệu khi component mount hoặc thay đổi trang
  useEffect(() => {
    dispatch(getListZnsEvent({ currentPage, pageSize }));
  }, [dispatch, currentPage, pageSize]);

  // Filter dữ liệu theo search text
  useEffect(() => {
    if (!searchText) {
      setFilteredData(listZnsEvents || []);
    } else {
      const filtered = (listZnsEvents || []).filter(item =>
        item.code?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        (Array.isArray(item.alias) ? item.alias.some(alias => 
          alias?.toLowerCase().includes(searchText.toLowerCase())
        ) : item.alias?.toLowerCase().includes(searchText.toLowerCase()))
      );
      setFilteredData(filtered);
    }
  }, [listZnsEvents, searchText]);

  // Xử lý thay đổi trang
  const handlePageChange = useCallback((page) => {
    dispatch(setCurrentPage(page));
  }, [dispatch]);

  // Xử lý thay đổi kích thước trang
  const handleSizeChange = useCallback((current, size) => {
    dispatch(setPageSize(size));
  }, [dispatch]);

  // Kiểm tra dòng đang được chỉnh sửa
  const isEditing = useCallback((record) => record.id === editingKey, [editingKey]);

  // Bắt đầu chỉnh sửa
  const edit = useCallback((record) => {
    const formData = {
      ...record,
      // Chuyển alias array thành string để edit
      alias: Array.isArray(record.alias) ? record.alias.join(', ') : record.alias || ''
    };
    form.setFieldsValue(formData);
    setEditingKey(record.id);
    setEditedRecord(record);
  }, [form]);

  // Hủy chỉnh sửa
  const cancel = useCallback(() => {
    setEditingKey("");
    form.resetFields();
  }, [form]);

  // Lưu dữ liệu đã chỉnh sửa
  const save = useCallback(async (id) => {
    try {
      const row = await form.validateFields();
      
      // Xử lý alias: chuyển từ string thành array
      const updatedData = {
        ...row,
        alias: row.alias 
          ? row.alias.split(',').map(item => item.trim()).filter(item => item.length > 0)
          : []
      };

      const response = await dispatch(updateZnsEvent({ id, data: updatedData }));
      
      if (updateZnsEvent.fulfilled.match(response)) {
        message.success("Cập nhật sự kiện thành công");
        await dispatch(getListZnsEvent({ currentPage, pageSize }));
        setEditingKey("");
      } else if (updateZnsEvent.rejected.match(response)) {
        handleUpdateErrors(response);
      }
    } catch (err) {
      console.error("Error saving record:", err);
      message.error("Có lỗi xảy ra khi lưu dữ liệu");
    }
  }, [form, dispatch, currentPage, pageSize]);

  // Xử lý lỗi khi update
  const handleUpdateErrors = useCallback((response) => {
    if (response.payload?.errors) {
      const errorFields = [];
      Object.entries(response.payload.errors).forEach(([field, messages]) => {
        const fieldMessages = Array.isArray(messages) ? messages : [messages];
        errorFields.push({
          name: field,
          errors: fieldMessages
        });
        fieldMessages.forEach(msg => message.error(msg));
      });
      form.setFields(errorFields);
    } else if (response.payload?.message) {
      message.error(response.payload.message);
    }
  }, [form]);

  // Xóa bản ghi
  const deleteEventRecord = useCallback(async (id) => {
    try {
      const response = await dispatch(deleteZnsEvent(id));
      if (deleteZnsEvent.fulfilled.match(response)) {
        message.success("Xóa sự kiện thành công");
        dispatch(getListZnsEvent({ currentPage, pageSize }));
      } else {
        message.error("Có lỗi xảy ra khi xóa sự kiện");
      }
    } catch (err) {
      console.error("Error deleting record:", err);
      message.error("Có lỗi xảy ra khi xóa sự kiện");
    }
  }, [dispatch, currentPage, pageSize]);

  // Render alias tags
  const renderAlias = useCallback((alias) => {
    if (!alias) return <StatusTag>Không có</StatusTag>;
    
    if (Array.isArray(alias)) {
      if (alias.length === 0) return <StatusTag>Không có</StatusTag>;
      
      return (
        <Space wrap>
          {alias.map((item, index) => (
            <StatusTag key={index}><TagOutlined /> 
              {item}
            </StatusTag>
          ))}
        </Space>
      );
    }
    
    // Fallback cho string
    return <StatusTag><TagOutlined /> {alias}</StatusTag>;
  }, []);

  // Định nghĩa các cột cho bảng
  const columns = useMemo(() => [
    {
      title: "Mã sự kiện",
      dataIndex: "code",
      key: "code",
      fixed: 'left',
      width: 300,
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="code"
            rules={[
              { required: true, message: "Vui lòng nhập mã sự kiện" },
              { max: 255, message: "Mã sự kiện không được vượt quá 255 ký tự" },
              {
                pattern: /^[a-zA-Z0-9_-]+$/,
                message: "Mã sự kiện chỉ được chứa chữ cái, số, dấu gạch ngang và dấu gạch dưới"
              }
            ]}
            style={{ margin: 0 }}
          >
            <Input maxLength={255} showCount />
          </Form.Item>
        ) : (
          <Typography.Text code>{record.code}</Typography.Text>
        );
      },
    },
    {
      title: "Tên sự kiện",
      dataIndex: "name",
      key: "name",
      width: 300,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="name"
            rules={[
              { required: true, message: "Vui lòng nhập tên sự kiện" },
              { min: 2, message: "Tên sự kiện phải có ít nhất 2 ký tự" },
              { max: 500, message: "Tên sự kiện không được vượt quá 500 ký tự" }
            ]}
            style={{ margin: 0 }}
          >
            <Input maxLength={500} showCount />
          </Form.Item>
        ) : (
          <Typography.Text strong>{record.name}</Typography.Text>
        );
      },
    },
    {
      title: "Alias",
      dataIndex: "alias",
      key: "alias",
      width: 200,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="alias"
            rules={[
              { max: 1000, message: "Tổng độ dài alias không được vượt quá 1000 ký tự" }
            ]}
            style={{ margin: 0 }}
            help={<small>Các alias cách nhau bởi dấu phẩy (,)</small>}
          >
            <Input.TextArea 
              rows={2}
              placeholder="Nhập các alias cách nhau bởi dấu phẩy. VD: bien1, bien2, bien3"
              maxLength={1000}
              showCount
            />
          </Form.Item>
        ) : (
          renderAlias(record.alias)
        );
      },
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "center",
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Tooltip title="Lưu thay đổi">
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={() => save(record.id)}
              >
                Lưu
              </Button>
            </Tooltip>
            <Tooltip title="Hủy thay đổi">
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={cancel}
              >
                Hủy
              </Button>
            </Tooltip>
          </Space>
        ) : (
          <RowActions
            disabled={editingKey !== ""}
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                onClick: () => edit(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa sự kiện",
                danger: true,
                divider: true,
                onClick: () =>
                  confirmDelete(record.name || `sự kiện #${record.id}`, () =>
                    deleteEventRecord(record.id)
                  ),
              },
            ]}
          />
        );
      },
    },
  ], [isEditing, save, cancel, edit, deleteEventRecord, editingKey, renderAlias]);

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <Card className="mb-6 shadow-sm">
        {/* Header */}
        <div className="mb-6">
          <Breadcrumb separator=">" className="mb-4">
            <Breadcrumb.Item>Trang chủ</Breadcrumb.Item>
            <Breadcrumb.Item>Cài đặt</Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to="/zns-events">Sự kiện ZNS</NavLink>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>

        {/* Actions */}
        <Row gutter={[16, 16]} className="mb-4" align="middle">
          <Col xs={24} sm={12} md={12} lg={12}>
            <Button
              type="primary"
              onClick={showModal}
              icon={<PlusCircleOutlined />}
              // style={{ height: '36px' }}
            >
              Thêm sự kiện mới
            </Button>
          </Col>
          
          <Col xs={24} sm={12} md={12} lg={12}>
            <Input
              placeholder="Tìm kiếm theo mã, tên hoặc alias..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              // style={{ height: '36px' }}
            />
          </Col>
        </Row>

        <Divider />

        {/* Table */}
        <Form form={form} component={false}>
          <Table
            columns={columns}
            loading={isLoadZnsEvent}
            dataSource={searchText ? filteredData : listZnsEvents}
            rowKey="id"
            size="small"
            bordered
            pagination={{
              showTotal: (total, range) =>
                `Hiển thị ${range[0]}-${range[1]} trong tổng ${total} bản ghi`,
              showQuickJumper: true,
              showSizeChanger: true,
              onShowSizeChange: handleSizeChange,
              total: searchText ? filteredData.length : totalRecords,
              current: currentPage,
              onChange: handlePageChange,
              pageSize: pageSize,
              pageSizeOptions: ['10', '20', '30', '50'],
              className: "mt-4"
            }}
            locale={{
              emptyText: (
                <Empty 
                  description={
                    searchText ? "Không tìm thấy sự kiện nào" : "Chưa có sự kiện nào"
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            scroll={{ x: 'max-content' }}
            rowClassName={(record, index) => 
              index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
            }
          />
        </Form>

        {/* Modal thêm mới */}
        <FormAddZnsEvent
          isModalOpen={isModalOpen}
          currentPage={currentPage}
          pageSize={pageSize}
          handleCancel={handleCancel}
        />
      </Card>

      <style jsx>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-dark {
          background-color: #ffffff;
        }
        .ant-table-tbody > tr:hover > td {
          background-color: #e6f7ff !important;
        }
      `}</style>
    </div>
  );
}